import React from 'react';
import { Menu as HeadlessMenu } from '@headlessui/react';
import {
  ChevronDown,
  Menu as MenuIcon,
  Check,
  ExternalLink,
  Pencil,
  Trash,
  Braces,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { useDirectTheme } from '../../../hooks/useDirectTheme';
import { useButtonColors } from '../../../hooks/useButtonColors';
import { PromptCardProps } from './types';
import { extractVariables } from '../../../utils/variables';
import { handlePromptAction, handleOpenInChatGPT } from './utils';
import { getPromptCardStyles } from './styles';
import { VerticalMenu } from './VerticalMenu';
import { HorizontalMenu } from './HorizontalMenu';
import { Z_LAYERS } from '../../../constants/zLayers';
import { cn } from '../../../utils/cn';

function DropdownMenu({
  onUse,
  onOpenInChatGPT,
  onEdit,
  onDelete,
}: {
  onUse: () => void;
  onOpenInChatGPT: () => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const { getDirectColor } = useDirectTheme();
  const styles = getPromptCardStyles('main');

  return (
    <HeadlessMenu as="div" className={styles.menuContainer}>
      <HeadlessMenu.Button className={styles.menuButton} aria-label="Open prompt actions menu">
        <MenuIcon
          className="w-4 h-4"
          style={{ color: getDirectColor('actionIcon') }}
          aria-hidden="true"
        />
      </HeadlessMenu.Button>

      <HeadlessMenu.Items
        className={styles.menuItems}
        style={{
          backgroundColor: getDirectColor('menuBackground'),
          zIndex: Z_LAYERS.DROPDOWN,
        }}
        aria-label="Prompt actions"
      >
        <HeadlessMenu.Item>
          {({ active }) => (
            <button
              className={styles.menuItem(active)}
              onClick={onUse}
              style={{
                color: active ? getDirectColor('menuTextHover') : getDirectColor('menuText'),
                backgroundColor: active
                  ? getDirectColor('menuBackgroundHover')
                  : getDirectColor('menuBackground'),
              }}
            >
              <Check
                className="w-4 h-4"
                style={{ color: getDirectColor('actionIconHover') }}
                aria-hidden="true"
              />
              Copy to Clipboard
            </button>
          )}
        </HeadlessMenu.Item>

        <HeadlessMenu.Item>
          {({ active }) => (
            <button
              className={styles.menuItem(active)}
              onClick={onOpenInChatGPT}
              style={{
                color: active ? getDirectColor('menuTextHover') : getDirectColor('menuText'),
                backgroundColor: active
                  ? getDirectColor('menuBackgroundHover')
                  : getDirectColor('menuBackground'),
              }}
            >
              <ExternalLink
                className="w-4 h-4"
                style={{ color: getDirectColor('actionIconHover') }}
                aria-hidden="true"
              />
              Open in ChatGPT
            </button>
          )}
        </HeadlessMenu.Item>

        <HeadlessMenu.Item>
          {({ active }) => (
            <button
              className={styles.menuItem(active)}
              onClick={onEdit}
              style={{
                color: active ? getDirectColor('menuTextHover') : getDirectColor('menuText'),
                backgroundColor: active
                  ? getDirectColor('menuBackgroundHover')
                  : getDirectColor('menuBackground'),
              }}
            >
              <Pencil
                className="w-4 h-4"
                style={{ color: getDirectColor('actionIconHover') }}
                aria-hidden="true"
              />
              Edit
            </button>
          )}
        </HeadlessMenu.Item>

        <div className={styles.divider} style={{ backgroundColor: getDirectColor('divider') }} />

        <HeadlessMenu.Item>
          {({ active }) => (
            <button
              className={styles.menuItem(active)}
              onClick={onDelete}
              style={{
                color: '#CC0000',
                backgroundColor: active
                  ? getDirectColor('menuBackgroundHover')
                  : getDirectColor('menuBackground'),
                fontWeight: 'bold',
                fontSize: '1.1rem',
                textShadow: '0px 0px 1px rgba(0,0,0,0.2)',
              }}
            >
              <Trash className="w-5 h-5" style={{ color: '#CC0000' }} aria-hidden="true" />
              Delete
            </button>
          )}
        </HeadlessMenu.Item>
      </HeadlessMenu.Items>
    </HeadlessMenu>
  );
}

export default function PromptCard({
  prompt,
  view,
  viewConfig,
  context: _context = {},
  selectedTags,
  onSelectTag,
  onEditPrompt,
  onDeletePrompt,
  onSelectPrompt,
  onShowVariableForm = () => {},
  onCopyToClipboard = async (_text: string) => {},
  menuStyle = 'horizontal',
  keyboardConfig = { enableGlobalShortcuts: true },
  themeConfig = { variant: 'default' },
  virtualized: _virtualized,
  virtualIndex: _virtualIndex,
  measureRef,
}: PromptCardProps) {
  const { getDirectColor } = useDirectTheme();
  const buttonColors = useButtonColors();
  const [expandedPromptId, setExpandedPromptId] = React.useState<string | null>(null);
  // Local ref to the card element so keyboard shortcuts can be scoped to the
  // hovered card; merged with the parent's measure callback ref below.
  const cardRef = React.useRef<HTMLDivElement | null>(null);
  const setCardRef = React.useCallback(
    (el: HTMLDivElement | null) => {
      cardRef.current = el;
      measureRef?.(el);
    },
    [measureRef]
  );

  // Memoize the variable check to avoid unnecessary re-renders
  const hasVariables = React.useMemo(() => {
    return extractVariables(prompt.text).length > 0;
  }, [prompt.text]);
  const styles = getPromptCardStyles(view, themeConfig.variant);

  const handleUsePrompt = React.useCallback(() => {
    handlePromptAction(prompt, onShowVariableForm, onCopyToClipboard, onSelectPrompt);
  }, [prompt, onShowVariableForm, onCopyToClipboard, onSelectPrompt]);

  const handleChatGPT = React.useCallback(() => {
    handleOpenInChatGPT(prompt, onShowVariableForm);
  }, [prompt, onShowVariableForm]);

  // Handle keyboard shortcuts if enabled
  React.useEffect(() => {
    if (!keyboardConfig.enableGlobalShortcuts) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Bail when typing in a form field: otherwise Ctrl+C inside a textarea
      // or the search box would preventDefault (blocking native copy) and fire
      // the card action.
      const target = e.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.tagName === 'SELECT' ||
          target.isContentEditable)
      ) {
        return;
      }

      // Every card registers this document-level listener; without a scope,
      // one keypress would fire the action for ALL visible cards (racing
      // clipboard writes, opening the variable form for the last one). Only
      // the card under the cursor responds.
      if (!cardRef.current?.matches(':hover')) return;

      // Add defensive check for keyboardConfig and shortcuts
      const shortcuts = keyboardConfig?.shortcuts || {};

      // Add defensive checks for each shortcut array and e.key
      const isCopyShortcut =
        shortcuts.copy &&
        Array.isArray(shortcuts.copy) &&
        e.key &&
        shortcuts.copy.some(
          key =>
            key &&
            typeof key === 'string' &&
            e.key &&
            typeof e.key === 'string' &&
            e.key.toLowerCase() === key.toLowerCase() &&
            (e.metaKey || e.ctrlKey)
        );

      const isEditShortcut =
        shortcuts.edit &&
        Array.isArray(shortcuts.edit) &&
        e.key &&
        shortcuts.edit.some(
          key =>
            key &&
            typeof key === 'string' &&
            e.key &&
            typeof e.key === 'string' &&
            e.key.toLowerCase() === key.toLowerCase() &&
            (e.metaKey || e.ctrlKey)
        );

      const isDeleteShortcut =
        shortcuts.delete &&
        Array.isArray(shortcuts.delete) &&
        e.key &&
        shortcuts.delete.some(
          key =>
            key &&
            typeof key === 'string' &&
            e.key &&
            typeof e.key === 'string' &&
            e.key.toLowerCase() === key.toLowerCase() &&
            (e.metaKey || e.ctrlKey)
        );

      if (isCopyShortcut) {
        e.preventDefault();
        handleUsePrompt();
      } else if (isEditShortcut) {
        e.preventDefault();
        onEditPrompt(prompt);
      } else if (isDeleteShortcut) {
        e.preventDefault();
        onDeletePrompt(prompt);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleUsePrompt, onEditPrompt, onDeletePrompt, prompt, keyboardConfig]);

  return (
    <motion.div
      className={styles.wrapper}
      initial={viewConfig?.animation?.initial || { opacity: 0, y: 20 }}
      animate={viewConfig?.animation?.animate || { opacity: 1, y: 0 }}
      exit={viewConfig?.animation?.exit}
      transition={viewConfig?.animation?.transition || { duration: 0.3 }}
      ref={setCardRef}
      style={{ zIndex: Z_LAYERS.CARD }}
    >
      <motion.div
        className={styles.card}
        style={{
          backgroundColor: getDirectColor('cardBackground'),
        }}
      >
        {hasVariables && (
          <div
            className="absolute top-2 right-2"
            style={{ zIndex: Z_LAYERS.MENU_BUTTON }}
            title="This prompt contains variables"
          >
            <motion.div
              initial={{ opacity: 0.8, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              whileHover={{ scale: 1.1 }}
              transition={{
                type: 'spring',
                stiffness: 300,
                damping: 15,
              }}
            >
              <Braces
                className="w-5 h-5"
                style={{ color: getDirectColor('actionIcon') }}
                aria-label="This prompt contains variables"
              />
            </motion.div>
          </div>
        )}

        <article className="relative" role="article" aria-labelledby={`prompt-title-${prompt.id}`}>
          <h3
            id={`prompt-title-${prompt.id}`}
            className={styles.title}
            onClick={handleUsePrompt}
            style={{ color: getDirectColor('title') }}
            role="button"
            tabIndex={0}
            aria-label={`Use prompt: ${prompt.title}`}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleUsePrompt();
              }
            }}
          >
            {prompt.title}
          </h3>
          <p className={styles.description} style={{ color: getDirectColor('description') }}>
            {prompt.description}
          </p>

          <div className={styles.tagsContainer} role="group" aria-label="Prompt tags">
            {prompt.tags.map(tag => (
              <motion.button
                key={tag}
                onClick={() => onSelectTag(tag)}
                className={styles.tag(selectedTags.includes(tag))}
                style={
                  selectedTags.includes(tag)
                    ? {
                        backgroundColor: buttonColors.primary.background,
                        color: buttonColors.primary.text,
                      }
                    : {
                        backgroundColor: buttonColors.primary.background,
                        color: buttonColors.primary.text,
                        opacity: 0.7,
                      }
                }
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                aria-label={`Filter by tag: ${tag}`}
                aria-pressed={selectedTags.includes(tag)}
              >
                {tag}
              </motion.button>
            ))}
          </div>
          {menuStyle === 'vertical' ? (
            <div className="absolute -left-[55px] top-0" style={{ zIndex: Z_LAYERS.MENU_BUTTON }}>
              <VerticalMenu
                onUse={handleUsePrompt}
                onOpenInChatGPT={handleChatGPT}
                onEdit={() => onEditPrompt(prompt)}
                onDelete={() => onDeletePrompt(prompt)}
              />
            </div>
          ) : menuStyle === 'horizontal' ? (
            <div
              className={cn('absolute bottom-0 right-0')}
              style={{ zIndex: Z_LAYERS.MENU_BUTTON }}
            >
              <HorizontalMenu
                onUse={handleUsePrompt}
                onOpenInChatGPT={handleChatGPT}
                onEdit={() => onEditPrompt(prompt)}
                onDelete={() => onDeletePrompt(prompt)}
                onExpand={() =>
                  setExpandedPromptId(expandedPromptId === prompt.id ? null : prompt.id)
                }
                isExpanded={expandedPromptId === prompt.id}
              />
            </div>
          ) : (
            <div
              className={cn('absolute bottom-0 right-3')}
              style={{ zIndex: Z_LAYERS.MENU_BUTTON }}
            >
              <DropdownMenu
                onUse={handleUsePrompt}
                onOpenInChatGPT={handleChatGPT}
                onEdit={() => onEditPrompt(prompt)}
                onDelete={() => onDeletePrompt(prompt)}
              />
            </div>
          )}

          {menuStyle !== 'horizontal' && (
            <motion.button
              className={styles.expandButton}
              onClick={() => setExpandedPromptId(expandedPromptId === prompt.id ? null : prompt.id)}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              aria-expanded={expandedPromptId === prompt.id}
              aria-controls={`prompt-content-${prompt.id}`}
              aria-label={
                expandedPromptId === prompt.id ? 'Show less details' : 'Show more details'
              }
            >
              <ChevronDown
                className={`w-5 h-5 transform transition-transform ${
                  expandedPromptId === prompt.id ? 'rotate-180' : ''
                }`}
                style={{ color: getDirectColor('actionIcon') }}
                aria-hidden="true"
              />
            </motion.button>
          )}
        </article>

        {expandedPromptId === prompt.id && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className={styles.expandedContent}
            id={`prompt-content-${prompt.id}`}
            role="region"
            aria-label="Prompt details"
          >
            <pre
              className={styles.codeBlock}
              style={{
                color: getDirectColor('codeText'),
                backgroundColor: getDirectColor('menuBackgroundHover'),
              }}
              role="textbox"
              aria-readonly="true"
              aria-label="Prompt text"
            >
              {prompt.text}
            </pre>
            <motion.button
              onClick={handleUsePrompt}
              className={styles.copyButton}
              style={{
                backgroundColor: getDirectColor('menuBackground'),
                color: getDirectColor('menuText'),
              }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              aria-label="Copy prompt to clipboard"
            >
              Copy to Clipboard
            </motion.button>
          </motion.div>
        )}
      </motion.div>
    </motion.div>
  );
}
